import { Inject, Injectable, Optional } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData' // ES 2015
import { SettingsService } from '../services/settings.service';
import 'dayjs/locale/nb';
import 'dayjs/locale/ar';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/ru';
import 'dayjs/locale/sv';
import 'dayjs/locale/da';
dayjs.extend(localeData);

// Add locale mappings
const LOCALE_MAPPINGS: { [key: string]: string } = {
    'no': 'nb', // Map 'no' to Norwegian Bokmål
    'nor': 'nb',
    'norwegian': 'nb'
};

@Injectable()
export class DayjsDateAdapter extends DateAdapter<Dayjs> {
    private dateFormat: string = 'DD/MM/YYYY'; // Default format
    private initialized = false;

    constructor(
        @Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string,
        @Optional() private settingsService?: SettingsService
    ) {
        super();
        this.setLocale(matDateLocale || dayjs.locale());
        
        if (this.settingsService) {
            this.settingsService.getSettings().subscribe(settings => {
                if (settings?.dateFormat) {
                    this.dateFormat = this.convertAngularFormatToDayjs(settings.dateFormat);
                    this.initialized = true;
                }
            });
        }
    }

    private convertAngularFormatToDayjs(format: string): string {
        return format
            .replace('dd', 'DD')
            .replace('MM', 'MM')
            .replace('yyyy', 'YYYY')
            .replace('yy', 'YY');
    }

    getYear(date: Dayjs): number {
        return date.year();
    }

    getMonth(date: Dayjs): number {
        return date.month();
    }

    getDate(date: Dayjs): number {
        return date.date();
    }

    getDayOfWeek(date: Dayjs): number {
        return date.day();
    }

    getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
        const format = style === 'long' ? 'MMMM' : 'MMM';
        return Array.from({ length: 12 }, (_, i) => 
            dayjs().month(i).format(format)
        );
    }

    getDateNames(): string[] {
        return Array.from({ length: 31 }, (_, i) => 
            String(i + 1)
        );
    }

    getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
        const format = style === 'long' ? 'dddd' : (style === 'short' ? 'ddd' : 'dd');
        return Array.from({ length: 7 }, (_, i) => 
            dayjs().day(i).format(format)
        );
    }

    getYearName(date: Dayjs): string {
        return date.format('YYYY');
    }

    getFirstDayOfWeek(): number {
        return dayjs.localeData().firstDayOfWeek();
    }

    getNumDaysInMonth(date: Dayjs): number {
        return date.daysInMonth();
    }

    clone(date: Dayjs): Dayjs {
        return date.clone();
    }

    createDate(year: number, month: number, date: number): Dayjs {
        return dayjs().year(year).month(month).date(date);
    }

    today(): Dayjs {
        return dayjs();
    }

    parse(value: any, parseFormat: string | null): Dayjs | null {
        if (value == null || value === '') {
            return null;
        }

        let dayJsValue: Dayjs | null = null;

        try {
            if (typeof value === 'number') {
                dayJsValue = dayjs(value);
            } else if (value instanceof Date) {
                dayJsValue = dayjs(value);
            } else if (dayjs.isDayjs(value)) {
                dayJsValue = value;
            } else if (typeof value === 'string') {
                // Try with provided format first
                if (parseFormat) {
                    dayJsValue = dayjs(value, parseFormat);
                }
                // Then try with settings format
                if (!dayJsValue?.isValid() && this.dateFormat) {
                    dayJsValue = dayjs(value, this.dateFormat);
                }
                // Finally try auto-parsing
                if (!dayJsValue?.isValid()) {
                    dayJsValue = dayjs(value);
                }
            }
        } catch (error) {
            console.warn('Date parsing error:', error);
            return null;
        }

        return dayJsValue?.isValid() ? dayJsValue : null;
    }

    format(date: Dayjs, displayFormat: string | null): string {
        if (!this.isValid(date)) {
            return '';
        }
        
        try {
            const format = displayFormat || this.dateFormat;
            return date.format(format);
        } catch (error) {
            console.warn('Date format error:', error);
            return date.format('DD/MM/YYYY'); // Fallback format
        }
    }

    addCalendarYears(date: Dayjs, years: number): Dayjs {
        return date.add(years, 'year');
    }

    addCalendarMonths(date: Dayjs, months: number): Dayjs {
        return date.add(months, 'month');
    }

    addCalendarDays(date: Dayjs, days: number): Dayjs {
        return date.add(days, 'day');
    }

    toIso8601(date: Dayjs): string {
        return date.toISOString();
    }

    isDateInstance(obj: any): boolean {
        return dayjs.isDayjs(obj);
    }

    isValid(date: Dayjs): boolean {
        return date && date.isValid();
    }

    invalid(): Dayjs {
        return dayjs(null);
    }

    override setLocale(locale: string) {
        // Map locale if needed
        const mappedLocale = LOCALE_MAPPINGS[locale.toLowerCase()] || locale;
        super.setLocale(mappedLocale);
        dayjs.locale(mappedLocale);
    }
} 